/**
 * The horizon caveat — the first block on EVERY Operations section.
 *
 * Three feeds sit behind this report and they settle at different times: the
 * partner's portal sales, the dark-store crawl, and the purchase orders. The
 * page states where each one stops, because two figures covering different
 * windows should never sit side by side unsigned.
 *
 * It is rendered once by OpsView rather than by each page, so the caveat cannot
 * drift between sections or quietly go missing from one of them.
 */
import { Banner } from '../ds';
import { useReport } from '../useReport';
import { type Basis, type OpsWindow, canCompare, comparisonLabel, str } from './opsLib';
import type { DateRange } from '@/lib/dateRange';

export function OpsBanner({ customer, range, basis, page }: {
  customer: string;
  range: DateRange;
  basis: Basis;
  page: string;
}) {
  // The same query the control tower reads, so the banner and the KPI strip can
  // never disagree about where the data stops.
  const q = useReport('/v1/reports/ops/control-tower', customer, range);
  if (q.isLoading || q.isError) return null;

  const w: OpsWindow | undefined = q.data?.window;
  const fill = q.data?.fill_rate_basis;
  const closes = fill?.last_close ?? null;
  const compare = canCompare(w);
  const cur = (q.data?.periods ?? []).find((p) => p['period'] === 'current');
  const portal = str(cur?.['basis']) === 'portal_actual';

  return (
    <Banner>
      <b>Everything on this page runs to {w?.as_of ?? '—'}.</b>{' '}
      Three feeds sit behind it &mdash; the partner&rsquo;s portal sales, the dark-store crawl and
      the purchase orders &mdash; and they settle at different times, so each figure states which
      one it is on rather than leaving two windows to be read as one.{' '}
      {closes
        ? (
          <>
            <b>Fill rate counts the purchase orders that CLOSED in this period</b> (to {closes}) —
            delivered over ordered across those POs. An order raised in the window but not yet
            closed is not counted and is not a miss; it lands in whichever period it closes in.{' '}
          </>
        )
        : (
          <>
            <b>No purchase order closed in this period</b>, so fill rate has nothing to report for
            it &mdash; that is not a zero.{' '}
          </>
        )}
      {!compare && (
        <>
          <br />
          <b>No comparison available:</b> the previous window falls outside the history this source
          holds, so no change is shown anywhere on the page.{' '}
        </>
      )}
      {compare && page === 'control-tower' && basis === 'mrp' && portal && (
        <>
          <br />
          <b>GMV and units are the portal actuals</b>, which the API serves at MRP for the current
          window only &mdash; so those two tiles carry no change against{' '}
          {comparisonLabel(w)}. Switch the basis to SP to read the change on the dark-store crawl,
          which covers both windows.{' '}
        </>
      )}
      {page === 'control-tower' && basis === 'sp' && (
        <>
          <br />
          <b>On the SP basis the headline tiles are the dark-store crawl estimate</b>, not the
          portal download: a narrower footprint as well as a different basis, so they read below the
          MRP figures for two reasons at once.{' '}
        </>
      )}
      {w?.note !== undefined && w.note !== '' && (
        <>
          <br />
          {w.note}
        </>
      )}
    </Banner>
  );
}
